import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Formik, Form } from "formik";
import SubmitButton from "../../components/Forms/SubmitButton";
import SettingsSelect from "../../components/Forms/SettingsSelect";

const BayRow = ({ bay }) => {
    const tenantContent = bay.is_occupied ? (
        <a
            href={`/booking/movedin?user_id=${bay.tenant.user_id}&booking_id=${bay.tenant.booking_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
        >
            {bay.tenant.name}
        </a>
    ) : (
        <span className="text-gray-400 italic">Vacant</span>
    );

    // Determine addendum status badge
    const getAddendumBadge = () => {
        if (!bay.addendum) {
            return <span className="text-gray-400">-</span>;
        }

        const status = bay.addendum.status;
        let badgeClass = "";
        let badgeText = "";

        switch (status) {
            case "Signed":
                badgeClass = "bg-green-100 text-green-800";
                badgeText = "Signed";
                break;
            case "Pending":
                badgeClass = "bg-yellow-100 text-yellow-800";
                badgeText = "Pending";
                break;
            case "No Addendum":
                badgeClass = "bg-gray-100 text-gray-800";
                badgeText = "No Addendum";
                break;
            default:
                badgeClass = "bg-gray-100 text-gray-500";
                badgeText = "N/A";
        }

        return (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
                {badgeText}
            </span>
        );
    };

    return (
        <tr className="border-b border-gray-100 hover:bg-gray-50">
            <td className="px-4 py-2 text-sm font-medium text-gray-900">{bay.bay_number}</td>
            <td className="px-4 py-2 text-sm">{tenantContent}</td>
            <td className="px-4 py-2 text-sm text-gray-600">{bay.tenant?.booking_reference || "-"}</td>
            <td className="px-4 py-2 text-sm text-gray-600">{bay.tenant?.unit || "-"}</td>
            <td className="px-4 py-2 text-sm text-gray-600">{bay.tenant?.cell || "-"}</td>
            <td className="px-4 py-2 text-sm text-gray-600">
                {bay.vehicle ? `${bay.vehicle.license_plate}` : "-"}
            </td>
            <td className="px-4 py-2 text-sm text-gray-600">
                {bay.vehicle ? [bay.vehicle.make, bay.vehicle.model, bay.vehicle.colour].filter(Boolean).join(" ") : "-"}
            </td>
            <td className="px-4 py-2 text-sm text-gray-600">{bay.tenant?.lease_start || "-"}</td>
            <td className="px-4 py-2 text-sm text-gray-600">{bay.tenant?.lease_end || "-"}</td>
            <td className="px-4 py-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${bay.is_occupied ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}`}>
                    {bay.is_occupied ? "Occupied" : "Vacant"}
                </span>
            </td>
            <td className="px-4 py-2">
                {getAddendumBadge()}
            </td>
        </tr>
    );
};

const FloorSection = ({ floor, expanded, toggleExpand, propertyId }) => {
    const occupancyPct = floor.summary.total_bays > 0 ? Math.round((floor.summary.occupied / floor.summary.total_bays) * 100) : 0;
    const floorKey = `floor-${propertyId}-${floor.id}`;

    return (
        <div className="border-b border-gray-200 last:border-b-0">
            <div
                className="px-6 py-3 bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100"
                onClick={() => toggleExpand(floorKey)}
            >
                <div className="flex items-center space-x-2">
                    <span className="text-sm transform transition-transform duration-200">
                        {expanded[floorKey] ? "▼" : "▶"}
                    </span>
                    <h4 className="text-md font-medium text-gray-900">{floor.name}</h4>
                </div>
                <div className="flex items-center space-x-4 text-sm">
                    <span className="text-gray-500">Total: <span className="font-medium text-gray-900">{floor.summary.total_bays}</span></span>
                    <span className="text-blue-600">Occupied: <span className="font-medium">{floor.summary.occupied}</span></span>
                    <span className="text-green-600">Vacant: <span className="font-medium">{floor.summary.vacant}</span></span>
                    <span className="text-gray-500">{occupancyPct}% full</span>
                </div>
            </div>
            {expanded[floorKey] && (
                <table className="min-w-full">
                    <thead>
                        <tr className="bg-white border-b border-gray-200">
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bay #</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Booking Ref</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">License Plate</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lease Start</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Lease End</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Addendum Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {floor.bays.map((bay) => (
                            <BayRow key={bay.id} bay={bay} />
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

const PropertyCard = ({ property, expanded, toggleExpand }) => {
    const occupancyPct = property.summary.total_bays > 0 ? Math.round((property.summary.occupied / property.summary.total_bays) * 100) : 0;

    return (
        <div className="bg-white shadow sm:rounded-lg">
            <div
                className="px-4 py-5 sm:p-6 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleExpand(`property-${property.id}`)}
            >
                <div className="flex items-center justify-between">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">{property.name}</h3>
                    <span className="transform transition-transform duration-200">
                        {expanded[`property-${property.id}`] ? "▼" : "▶"}
                    </span>
                </div>
                <div className="mt-2 grid grid-cols-4 gap-4 text-sm">
                    <div>
                        <span className="text-gray-500">Total Bays: </span>
                        <span className="font-medium text-gray-900">{property.summary.total_bays}</span>
                    </div>
                    <div>
                        <span className="text-blue-600">Occupied: </span>
                        <span className="font-medium">{property.summary.occupied}</span>
                    </div>
                    <div>
                        <span className="text-green-600">Vacant: </span>
                        <span className="font-medium">{property.summary.vacant}</span>
                    </div>
                    <div>
                        <span className="text-gray-500">Occupancy: </span>
                        <span className="font-medium">{occupancyPct}%</span>
                    </div>
                </div>
            </div>

            {expanded[`property-${property.id}`] && (
                <div className="border-t border-gray-200">
                    {property.floors.map((floor) => (
                        <FloorSection key={floor.id} floor={floor} expanded={expanded} toggleExpand={toggleExpand} propertyId={property.id} />
                    ))}
                </div>
            )}
        </div>
    );
};

const OverallSummary = ({ totals }) => {
    const occupancyPct = totals.total_bays > 0 ? Math.round((totals.occupied / totals.total_bays) * 100) : 0;

    const summaryItems = [
        { label: "Total Bays", value: totals.total_bays, className: "bg-gray-50" },
        { label: "Occupied", value: totals.occupied, className: "bg-blue-50" },
        { label: "Vacant", value: totals.vacant, className: "bg-green-50" },
        { label: "Occupancy Rate", value: `${occupancyPct}%`, className: "bg-purple-50" },
    ];

    return (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Overall Summary</h3>
                <div className="mt-4 grid grid-cols-4 gap-4">
                    {summaryItems.map((item) => (
                        <div key={item.label} className={`${item.className} px-4 py-2 rounded-lg`}>
                            <dt className="text-sm font-medium text-gray-500">{item.label}</dt>
                            <dd className="mt-1 text-2xl font-semibold text-gray-900">{item.value}</dd>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default function ReportParkingBays() {
    const [loading, setLoading] = useState(false);
    const [properties, setProperties] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        loadProperties();
    }, []);

    const loadProperties = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}properties`);
            const allProperties = [
                { id: 0, label: "All Buildings", value: 0 },
                ...response.data.map((prop) => ({
                    id: prop.value,
                    label: prop.label,
                    value: prop.value,
                    name: prop.label,
                })),
            ];
            setProperties(allProperties);
        } catch (error) {
            console.error("Error loading properties:", error);
        }
    };

    const toggleExpand = (key) => {
        setExpanded((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const handleExportCsv = () => {
        if (!reportData) return;

        const rows = [["Building", "Floor", "Bay #", "Tenant Name", "Booking Ref", "Unit", "Contact", "License Plate", "Vehicle", "Lease Start", "Lease End", "Status", "Addendum Status"]];

        reportData.properties.forEach((property) => {
            property.floors.forEach((floor) => {
                floor.bays.forEach((bay) => {
                    rows.push([
                        property.name,
                        floor.name,
                        bay.bay_number,
                        bay.tenant?.name || "",
                        bay.tenant?.booking_reference || "",
                        bay.tenant?.unit || "",
                        bay.tenant?.cell || "",
                        bay.vehicle?.license_plate || "",
                        bay.vehicle ? [bay.vehicle.make, bay.vehicle.model, bay.vehicle.colour].filter(Boolean).join(" ") : "",
                        bay.tenant?.lease_start || "",
                        bay.tenant?.lease_end || "",
                        bay.is_occupied ? "Occupied" : "Vacant",
                        bay.addendum?.status || "N/A",
                    ]);
                });
            });
        });

        const csvContent = rows.map((row) => row.map((cell) => `"${(cell || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Parking_Bay_Report.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleGenerateReport = async (values, { setSubmitting }) => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}reports/parking-bays`, {
                params: {
                    property_id: values.property_id === 0 ? null : values.property_id,
                },
            });
            setReportData(response.data);
        } catch (error) {
            console.error("Error loading report:", error);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    return (
        <div className="w-auto bg-gray-100 my-6 xl:my-0 xl:mx-4 rounded-3xl flex-1 p-5">
            <h1 className="text-2xl font-semibold text-gray-900 mb-6">Parking Bay Report</h1>

            <Formik enableReinitialize initialValues={{ property_id: 0 }} onSubmit={handleGenerateReport}>
                {({ values, setFieldValue, handleSubmit, isValid, isSubmitting }) => (
                    <Form onSubmit={handleSubmit}>
                        <div className="grid lg:grid-cols-12 md:lg:grid-cols-6 gap-12 mt-5 mb-5">
                            <SettingsSelect
                                label="Select Building (Optional)"
                                name="property_id"
                                onChange={setFieldValue}
                                value={values.property_id}
                                options={properties}
                            />
                        </div>

                        <div className="flex items-center space-x-3">
                            <SubmitButton name="Submit" disabled={!isValid || isSubmitting} isSubmitting={isSubmitting} text="Generate Report" />

                            {reportData && (
                                <button
                                    type="button"
                                    onClick={handleExportCsv}
                                    className="inline-flex items-center px-6 py-2.5 rounded-lg border text-sm font-medium focus:z-10 focus:outline-none border-green-600 bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-out shadow-sm"
                                >
                                    Export CSV
                                </button>
                            )}
                        </div>

                        {loading && (
                            <div className="mt-4 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                                <span className="ml-2">Generating report...</span>
                            </div>
                        )}

                        {reportData && !loading && (
                            <div className="mt-6">
                                <OverallSummary totals={reportData.totals} />
                                <div className="mt-6 space-y-4">
                                    {reportData.properties.map((property) => (
                                        <PropertyCard key={property.id} property={property} expanded={expanded} toggleExpand={toggleExpand} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </Form>
                )}
            </Formik>
        </div>
    );
}
